/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { DeadlinesTable, TermCalculator } from '@/components/Deadlines';
import { Button, ErrorText, Input, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

type T = 'open' | 'mine' | 'done' | 'calc' | 'calendar';

export function DeadlinesPage() {
  const [tab, setTab] = useState<T>('open');
  return (
    <>
      <PageHeader title="Процессуальные сроки" subtitle="Сроки считаются по производственному календарю с переносом на рабочий день" />
      <Tabs<T> value={tab} onChange={setTab} tabs={[{ id: 'open', label: 'Открытые' }, { id: 'mine', label: 'Мои' }, { id: 'done', label: 'Выполненные' }, { id: 'calc', label: 'Калькулятор' }, { id: 'calendar', label: 'Производственный календарь' }]} />
      {tab === 'open' && <DeadlinesTable filter={{ status: 'OPEN' }} />}
      {tab === 'mine' && <DeadlinesTable filter={{ status: 'OPEN', mine: 'true' }} />}
      {tab === 'done' && <DeadlinesTable filter={{ status: 'DONE' }} />}
      {tab === 'calc' && <div className="card max-w-2xl p-5"><TermCalculator /></div>}
      {tab === 'calendar' && <ProductionCalendar />}
    </>
  );
}

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

function ProductionCalendar() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [year, setYear] = useState(new Date().getFullYear());
  const { data, isLoading } = useQuery({ queryKey: ['prod-cal', year], queryFn: () => api.get(`/production-calendar/${year}`) });
  const [date, setDate] = useState(''); const [kind, setKind] = useState('off');
  const save = useMutation({ mutationFn: (b: any) => api.put('/production-calendar/days', { days: [b] }), onSuccess: () => { qc.invalidateQueries({ queryKey: ['prod-cal'] }); qc.invalidateQueries({ queryKey: ['term'] }); } });
  if (isLoading || !data) return <Spinner />;
  const off = new Set<string>(data.holidays); const on = new Set<string>(data.workdays);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => setYear(year - 1)}>{year - 1}</Button>
        <div className="text-lg font-semibold">{year}</div>
        <Button variant="secondary" size="sm" onClick={() => setYear(year + 1)}>{year + 1}</Button>
        <div className="text-sm text-slate-600">Рабочих дней: <b>{data.workdaysTotal}</b></div>
        {!data.known && <div className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">Календаря на {year} год в программе нет: добавьте праздники вручную ниже</div>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {MONTHS.map((name, m) => {
          const first = new Date(Date.UTC(year, m, 1)); const days = new Date(Date.UTC(year, m + 1, 0)).getUTCDate();
          const pad = (first.getUTCDay() + 6) % 7;
          return (
            <div key={m} className="card p-3">
              <div className="mb-1 text-sm font-medium">{name}</div>
              <div className="grid grid-cols-7 gap-0.5 text-center text-[11px]">
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => <div key={d} className="text-slate-400">{d}</div>)}
                {Array.from({ length: pad }).map((_, i) => <div key={`p${i}`} />)}
                {Array.from({ length: days }).map((_, i) => {
                  const d = `${year}-${String(m + 1).padStart(2, '0')}-${String(i + 1).padStart(2, '0')}`;
                  const wd = (pad + i) % 7; const weekend = wd >= 5;
                  const isOff = off.has(d) || (weekend && !on.has(d));
                  return <div key={d} title={off.has(d) ? 'праздник или перенос' : on.has(d) ? 'рабочий выходной' : ''}
                    className={clsx('rounded py-0.5', isOff ? 'bg-red-50 text-red-600' : 'text-slate-700', off.has(d) && 'font-semibold', on.has(d) && 'bg-green-50 font-semibold text-green-700')}>{i + 1}</div>;
                })}
              </div>
            </div>
          );
        })}
      </div>
      {can('calendar.manage') && (
        <div className="card flex flex-wrap items-end gap-3 p-4">
          <div><div className="label">Дата</div><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><div className="label">Сделать</div><Select value={kind} onChange={(e) => setKind(e.target.value)} options={[{ value: 'off', label: 'нерабочим' }, { value: 'on', label: 'рабочим' }, { value: 'reset', label: 'как в программе' }]} /></div>
          <Button disabled={!date || save.isPending} onClick={() => save.mutate({ date, isWorkday: kind === 'reset' ? null : kind === 'on' })}>Применить</Button>
          <ErrorText error={save.error} />
          {data.overrides.length > 0 && <div className="w-full text-xs text-slate-500">Поправки: {data.overrides.map((o: any) => `${o.date.split('-').reverse().join('.')} ${o.isWorkday ? 'рабочий' : 'нерабочий'}`).join(', ')}</div>}
        </div>
      )}
    </div>
  );
}
